import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import WysiwygEditor from '@/components/ui/wysiwyg-editor';
import ImageUpload from './ImageUpload';
import { NotificationTemplate } from '@/hooks/useNotificationTemplates';
import { useNotificationHeaders } from '@/hooks/useNotificationHeaders';
import { useNotificationFooters } from '@/hooks/useNotificationFooters';
import { X, Plus, Eye, Variable, Image as ImageIcon } from 'lucide-react';

interface NotificationTemplateEditorProps {
  template?: NotificationTemplate | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: Partial<NotificationTemplate>) => Promise<void>;
}

const TEMPLATE_CATEGORIES = [
  { value: 'registration', label: 'Registration' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'cancellation', label: 'Cancellation' },
  { value: 'recruiting', label: 'Recruiting' },
  { value: 'custom', label: 'Custom' }
];

const AVAILABLE_VARIABLES = [
  'customer_name',
  'event_name', 
  'event_date',
  'venue',
  'registration_tier',
  'amount_owed',
  'team_name',
  'coach_name',
  'participant_count'
];

const NotificationTemplateEditor: React.FC<NotificationTemplateEditorProps> = ({
  template,
  isOpen,
  onClose,
  onSave
}) => {
  const { headers } = useNotificationHeaders();
  const { footers } = useNotificationFooters();
  
  const [formData, setFormData] = useState({
    title: template?.title || '',
    category: template?.category || 'custom',
    type: template?.type || 'email',
    trigger_event: template?.trigger_event || null,
    audience: template?.audience || 'athlete',
    default_copy: template?.default_copy || '',
    delivery_channel: template?.delivery_channel || 'email',
    header_id: template?.header_id || 'none',
    footer_id: template?.footer_id || 'none',
    is_active: template?.is_active ?? true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewMode, setPreviewMode] = useState<'text' | 'html'>('text');

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const insertVariableIntoContent = (variable: string, isHtml = false) => {
    const variableTag = `{${variable}}`;
    const field = isHtml ? 'default_copy' : 'default_copy';
    
    setFormData(prev => ({
      ...prev,
      [field]: prev[field] + variableTag
    }));
  };

  const generatePreview = (content: string, isHtml = false) => {
    let preview = content;
    AVAILABLE_VARIABLES.forEach(variable => {
      const placeholder = variable === 'customer_name' ? 'John Doe' :
                         variable === 'event_name' ? 'Summer Basketball Tournament' :
                         variable === 'event_date' ? 'July 15, 2024' :
                         variable === 'venue' ? 'Sports Complex Arena' :
                         variable === 'registration_tier' ? 'Premium Package' :
                         variable === 'amount_owed' ? '$150.00' :
                         `[${variable}]`;
      
      preview = preview.replace(new RegExp(`{${variable}}`, 'g'), placeholder);
    });
    return preview;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.default_copy) return;

    setIsSubmitting(true);
    try {
      await onSave({
        ...formData,
        header_id: formData.header_id === 'none' ? null : formData.header_id,
        footer_id: formData.footer_id === 'none' ? null : formData.footer_id,
        trigger_event: formData.trigger_event === 'none' ? null : formData.trigger_event
      });
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {template ? 'Edit Template' : 'Create New Template'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Template Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., Registration Confirmation"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="events">Events</SelectItem>
                  <SelectItem value="registration">Registration</SelectItem>
                  <SelectItem value="reminders">Reminders</SelectItem>
                  <SelectItem value="payments">Payments</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Trigger Event & Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="trigger_event">Trigger Event (Automation)</Label>
              <Select 
                value={formData.trigger_event || 'none'} 
                onValueChange={(value) => handleInputChange('trigger_event', value === 'none' ? null : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select trigger event" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Manual Template (No Auto-Trigger)</SelectItem>
                  <SelectItem value="registration_confirmed">Registration Confirmed</SelectItem>
                  <SelectItem value="event_reminder">Event Reminder</SelectItem>
                  <SelectItem value="event_cancelled">Event Cancelled/Changed</SelectItem>
                  <SelectItem value="check_in_instructions">Check-in Instructions</SelectItem>
                  <SelectItem value="payment_confirmed">Payment Confirmed</SelectItem>
                  <SelectItem value="event_updated">Event Updated</SelectItem>
                </SelectContent>
              </Select>
              {formData.trigger_event && (
                <p className="text-xs text-blue-600 mt-1">
                  ✓ This template will be used automatically when this event occurs
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Template Type</Label>
              <Select 
                value={formData.type} 
                onValueChange={(value) => handleInputChange('type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">📧 Email</SelectItem>
                  <SelectItem value="sms">📱 SMS</SelectItem>
                  <SelectItem value="push">🔔 Push Notification</SelectItem>
                </SelectContent>
              </Select>
              {formData.type === 'sms' && (
                <p className="text-xs text-orange-600 mt-1">
                  ⚠️ SMS messages have a 160 character limit
                </p>
              )}
            </div>
          </div>

          {/* Subject Line (Email only) */}
          {formData.type === 'email' && (
            <div className="space-y-2">
              <Label htmlFor="audience">Audience</Label>
              <Input
                id="audience"
                value={formData.audience}
                onChange={(e) => handleInputChange('audience', e.target.value)}
                placeholder="e.g., athlete, parent, coach"
                required
              />
            </div>
          )}

          {/* Headers & Footers (Email only) */}
          {formData.type === 'email' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="header">Header Template</Label>
                <Select value={formData.header_id} onValueChange={(value) => handleInputChange('header_id', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select header (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Header</SelectItem>
                    {headers.map(header => (
                      <SelectItem key={header.id} value={header.id}>
                        {header.name} {header.is_default && '(Default)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="footer">Footer Template</Label>
                <Select value={formData.footer_id} onValueChange={(value) => handleInputChange('footer_id', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select footer (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Footer</SelectItem>
                    {footers.map(footer => (
                      <SelectItem key={footer.id} value={footer.id}>
                        {footer.name} {footer.is_default && '(Default)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Variables Help */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Variable className="h-4 w-4" />
                Available Variables
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Use these variables in your templates</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {AVAILABLE_VARIABLES.map(variable => (
                      <Badge key={variable} variant="outline" className="text-xs">
                        {`{${variable}}`}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Variables will be replaced with actual values when sending notifications.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Tabs */}
          <Tabs defaultValue="text" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="text">Text Content</TabsTrigger>
              {formData.type === 'email' && <TabsTrigger value="html">HTML Content</TabsTrigger>}
              {formData.type === 'email' && <TabsTrigger value="images">Images</TabsTrigger>}
            </TabsList>
            
            <TabsContent value="text" className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Text Content</Label>
                  <div className="flex gap-2">
                    {AVAILABLE_VARIABLES.map(variable => (
                      <Button
                        key={variable}
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => insertVariableIntoContent(variable, false)}
                        title={`Insert {${variable}}`}
                      >
                        {variable}
                      </Button>
                    ))}
                  </div>
                </div>
                <Textarea
                  value={formData.default_copy}
                  onChange={(e) => handleInputChange('default_copy', e.target.value)}
                  placeholder={formData.type === 'sms' 
                    ? "Enter SMS message (160 character limit)..."
                    : "Enter the text content of your template..."
                  }
                  className="min-h-[200px]"
                  maxLength={formData.type === 'sms' ? 160 : undefined}
                  required
                />
                {formData.type === 'sms' && (
                  <p className="text-xs text-gray-500">
                    {formData.default_copy.length}/160 characters
                  </p>
                )}
              </div>
              
              {/* Text Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="whitespace-pre-wrap text-sm bg-gray-50 p-4 rounded">
                    {generatePreview(formData.default_copy, false)}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            
          </Tabs>

          {/* Form Actions */}
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || !formData.title || !formData.default_copy}
            >
              {isSubmitting ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationTemplateEditor;